import { Chip } from "@mui/material";
import { GridRowId } from "@mui/x-data-grid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { enqueueSnackbar } from "notistack";
import { RefObject, createElement, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbHeartHandshake } from "react-icons/tb";

import CommonAlert from "@/components/CommonAlert";
import type { Req_UpdateHotelClinicTasksInBulkPayload } from "@/requests/hotel-clinic-task-update.types";
import { useUpdateHotelClinicTasksMutation } from "@/requests/hotel-clinic.mutations";
import {
    ClinicTasksEditableFields,
    useClinicArrivalTasksGridActions,
    useClinicArrivalTasksGridChanges,
    useClinicArrivalTasksGridSaveConfirmOpen,
} from "@/stores/use-clinic-arrival-tasks-grid";
import { useHotelClinicArrivalDate } from "@/stores/use-clinic-arrival-unassigned-passengers-table";

export type ClinicArrivalTasksUpdateDialogProps = {
    apiRef: RefObject<GridApiPremium | null>;
};

/** Icons for editable task fields (keep in sync with `ClinicTasksEditableFields`). */
export const clinicTasksEditableFieldIcons: Record<keyof ClinicTasksEditableFields, { icon: IconType }> = {
    pickUp: { icon: TbHeartHandshake },
};

export function ClinicArrivalTasksUpdateDialogChip({ label }: { label: keyof ClinicTasksEditableFields }) {
    const { t } = useTranslation();
    const item = clinicTasksEditableFieldIcons[label];

    if (!item) return null;

    return <Chip icon={createElement(item.icon)} label={t(label)} />;
}

function ClinicArrivalTasksUpdateDialog({ apiRef }: ClinicArrivalTasksUpdateDialogProps) {
    const clinicDate = useHotelClinicArrivalDate();
    const isoDate = clinicDate.toISODate() ?? undefined;
    const { isPending, mutateAsync } = useUpdateHotelClinicTasksMutation({ isoDate });
    const { t } = useTranslation();
    const open = useClinicArrivalTasksGridSaveConfirmOpen();
    const changes = useClinicArrivalTasksGridChanges();
    const { setSaveConfirmOpen, handleRowModesModelCancel } = useClinicArrivalTasksGridActions();

    const pendingCount = useMemo(() => Object.keys(changes).length, [changes]);

    const changeList = useMemo(
        () =>
            Object.values(changes).reduce(
                (prev, rowChanges) => {
                    if (!rowChanges) return prev;
                    const keys = Object.keys(rowChanges) as (keyof ClinicTasksEditableFields)[];
                    keys.forEach((key) => {
                        const val = rowChanges[key];
                        if (val === undefined) return;
                        if (typeof val === "string" && String(val).trim() === "") return;
                        prev.list.add(key);
                    });
                    return prev;
                },
                { list: new Set<keyof ClinicTasksEditableFields>() }
            ),
        [changes]
    );

    const handleClose = useCallback(() => {
        setSaveConfirmOpen(false);
    }, [setSaveConfirmOpen]);

    const handleConfirm = useCallback(async () => {
        if (isPending) return;
        const api = apiRef.current;

        const tasksPayload: Req_UpdateHotelClinicTasksInBulkPayload["tasks"] = [];
        for (const taskId of Object.keys(changes)) {
            const pickUp = changes[taskId]?.pickUp;
            if (pickUp == null || String(pickUp).trim() === "") continue;
            tasksPayload.push({ taskId: String(taskId), updateData: { pickUp: String(pickUp).trim() } });
        }

        if (!tasksPayload.length) {
            handleClose();
            return;
        }

        if (!api) return;

        try {
            const responses = await mutateAsync({ tasks: tasksPayload });
            const completedList: GridRowId[] = [];

            for (const [index, response] of responses.entries()) {
                if (response.ok) completedList.push(response.id);
                setTimeout(() => {
                    if (!response.ok) {
                        enqueueSnackbar(response.message || "", {
                            variant: "error",
                        });
                    }
                    if (index === responses.length - 1 && completedList.length) {
                        enqueueSnackbar(
                            t("responses.hotelClinicTasksPickUpUpdated", {
                                count: completedList.length || 1,
                            }),
                            {
                                variant: "success",
                            }
                        );
                    }
                }, index * 80);
            }

            for (const id of completedList) {
                void handleRowModesModelCancel(id, api, false);
            }
            handleClose();
        } catch (e) {
            console.error(e);
        }
    }, [apiRef, handleClose, handleRowModesModelCancel, isPending, changes, mutateAsync, t]);

    const dialogVisible = open && pendingCount > 0;

    return (
        <CommonAlert
            open={dialogVisible}
            loading={isPending}
            title="confirmations.clinicArrivalTasksPickUpTitle"
            description="confirmations.clinicArrivalTasksPickUpBody"
            color="primary"
            onClose={handleClose}
            handleConfirm={handleConfirm}
            html
            focusConfirmOnOpen
            descriptionOpts={{
                count: pendingCount,
            }}
        >
            <div className="flex flex-wrap gap-2 py-1">
                {[...changeList.list].map((label) => (
                    <ClinicArrivalTasksUpdateDialogChip key={label} label={label} />
                ))}
            </div>
        </CommonAlert>
    );
}

export default ClinicArrivalTasksUpdateDialog;
